import { PnlBreakdown } from "reports/getters/pnlBreakdownGetter/pnlBreakdownTypes";

import {
  DOWNTIME_PNL_CATEGORIES,
  PnlCategoriesEnum,
  PnlCategoryLabelsEnum,
} from "reports/types";
import { Bar, Cell, LabelList } from "recharts";
import GenericComposedChart, {
  renderCustomBar,
  TOP_XAXIS_PADDING,
} from "../GenericCharts/BarCharts/GenericComposedChart";
import {
  BREAKPOINT_MOBILE,
  PNL_CATEGORIES_TO_COLORS_MAP,
} from "reports/constants/constants";

import RechartLegend, {
  LegendIconEnum,
  LegendItemType,
} from "../RechartHelpers/RechartLegend";
import CardWrapper from "../CardWrapperStyles";
import styled from "styled-components/macro";
import { useState } from "react";
import { Link, useMediaQuery } from "@mui/material";
import { pnlBreakdownToDownTimesChartData } from "./bespokeReportUtils";
import { ONE_HOUR_OF_MS, ONE_MINUTE_OF_MS } from "reports/utils/timeUtils";
import { RechartToolTipStyles } from "../RechartHelpers/TooltipStyles";
import {
  RightBarLabel,
  RightInsideBarLabel,
} from "../RechartHelpers/RechartBarLabels";
import PrintableRechart from "../PrintableRechart";
import { formatHoursMinutes } from "utils/timeUtils";
import { useTranslation } from "react-i18next";
import { startCase } from "lodash";

export const ParetoStyles = styled.div`
  display: flex;
  justify-content: center;

  .showMore {
    display: flex;
    align-self: flex-end;
    top: -${TOP_XAXIS_PADDING}px !important;
  }
`;

const CHART_MIN_HEIGHT = 500;
const CHART_PRINT_HEIGHT = 250;

export default function Pareto({
  pnlBreakdown,
  categoriesToSkip,
  tagsToSkip,
  height,
  title = "Pareto Of Opportunities",
  xLabel = "Opportunities",
  timeDivisor = ONE_MINUTE_OF_MS,
  numberOfDowntimes,
  showDurationLabel = false,
  shouldShowHideLessThan1Percent = true,
  xAxisDomain,
  subHeader = "",
}: {
  pnlBreakdown: PnlBreakdown;
  categoriesToSkip?: PnlCategoriesEnum[];
  tagsToSkip?: string[];
  height?: number;
  title?: string;
  xLabel?: string;
  timeDivisor?: number;
  numberOfDowntimes?: number;
  showDurationLabel?: boolean;
  shouldShowHideLessThan1Percent?: boolean;
  xAxisDomain?: number[];
  subHeader?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isInMinutes = timeDivisor === ONE_MINUTE_OF_MS;
  const isMobileOrSmaller = useMediaQuery(
    `(max-width: ${BREAKPOINT_MOBILE}px)`
  );
  const isPrint = useMediaQuery("print");

  const downtimeCategoryLegendItems =
    useLegendItemsForPnlDowntimeCategories(categoriesToSkip);
  const tooltipContent = customTooltipContent(isInMinutes);

  const SINGLE_BAR_HEIGHT = 34;

  // downTimesToShow --> downtimes over and equal 1%
  // downTimesToHide --> downtimes less than 1%
  const {
    chartData: allDowntimes,
    downTimesToShow,
    downTimesToHide,
  } = pnlBreakdownToDownTimesChartData({
    pnlBreakdown,
    categoriesToSkip,
    tagsToSkip,
    showMinutes: isInMinutes,
    numberOfDowntimes,
  });

  const chartData = !shouldShowHideLessThan1Percent
    ? allDowntimes
    : isExpanded
      ? [...downTimesToShow, ...downTimesToHide]
      : downTimesToShow;

  const chartScreenHeight = SINGLE_BAR_HEIGHT * chartData.length;
  const chartPrintOrScreenHeight = isPrint
    ? CHART_PRINT_HEIGHT
    : (height ??
      (chartScreenHeight > CHART_MIN_HEIGHT
        ? chartScreenHeight
        : CHART_MIN_HEIGHT));

  const customBars = (
    <Bar
      dataKey="duration"
      isAnimationActive={false}
      name={"Minutes"}
      shape={renderCustomBar(chartPrintOrScreenHeight)}
    >
      {chartData?.map(({ category }, index) => (
        <Cell
          key={`customBarCell-${index}`}
          fill={PNL_CATEGORIES_TO_COLORS_MAP[category]}
        />
      ))}

      <LabelList dataKey={"percent"} content={RightBarLabel({ suffix: "%" })} />
      {showDurationLabel && (
        <LabelList
          dataKey={"duration"}
          content={RightInsideBarLabel({
            labelFormatter: value => {
              const valueMs =
                value * (isInMinutes ? ONE_MINUTE_OF_MS : ONE_HOUR_OF_MS);
              return formatHoursMinutes(valueMs);
            },
          })}
        />
      )}
    </Bar>
  );

  const shouldShowChart = chartData.length > 0;

  const ParetoChartContent = (width: number) => (
    <ParetoStyles>
      <GenericComposedChart
        data={chartData}
        xKey={"percent"}
        xLabel={`${xLabel} (${getTimeUnits({
          isInMinutes,
          isShortForm: false,
        })})`}
        yKey={"name"}
        yLabel={""}
        height={chartPrintOrScreenHeight}
        vertical={true}
        {...{ customBars, width }}
        legend={RechartLegend({
          width,
          customLegendItems: downtimeCategoryLegendItems,
        })}
        yAxisMaxCharsPerLine={isMobileOrSmaller ? 15 : 25}
        tooltipContent={tooltipContent}
        xAxisDomain={xAxisDomain}
      />

      {shouldShowHideLessThan1Percent && (
        <Link
          className="showMore"
          component="button"
          onClick={() => setIsExpanded(prev => !prev)}
          underline="hover"
        >
          {isExpanded ? "Show Less" : "Show More..."}
        </Link>
      )}
    </ParetoStyles>
  );

  return (
    <CardWrapper>
      <div className="header">{title}</div>
      {subHeader && <div className="subHeader">{subHeader}</div>}
      {shouldShowChart ? (
        <PrintableRechart
          chart={ParetoChartContent}
          containerWidthRatio={isMobileOrSmaller ? 1 : 0.55}
          printedChartWidthRatio={0.4}
        />
      ) : (
        <div style={{ textAlign: "center" }}>
          No downtimes for given categories
        </div>
      )}
    </CardWrapper>
  );
}

/**
 * Get time units
 *
 * @param {boolean} isInMinutes
 * @param {boolean} shortForm
 * @return {*}
 */
const getTimeUnits = ({
  isInMinutes,
  isShortForm = true,
}: {
  isInMinutes: boolean;
  isShortForm?: boolean;
}) => {
  if (isShortForm) {
    return isInMinutes ? "min" : "hrs";
  } else {
    return isInMinutes ? "Minutes" : "Hours";
  }
};

/**
 * Custom tooltip content rather than default
 *
 * @param {*} bars
 * @param {*} chartData
 */
const customTooltipContent =
  isInMinutes =>
  ({ active, payload }) => {
    if (active && payload && payload.length) {
      const category = PnlCategoryLabelsEnum[payload[0].payload.category];
      const duration = payload[0].payload.duration;
      const durationMs =
        duration * (isInMinutes ? ONE_MINUTE_OF_MS : ONE_HOUR_OF_MS);
      const frequency = payload[0].payload.frequency;

      return (
        <RechartToolTipStyles>
          <div className="tooltip-title">{category}</div>
          <div>{`Duration: ${formatHoursMinutes(durationMs)}`}</div>
          <div>{`Frequency: ${frequency}`}</div>
        </RechartToolTipStyles>
      );
    }

    return null;
  };

/**
 * Legend item per downtime category
 *
 * @return {*}  {LegendItemType[]}
 */
export function useLegendItemsForPnlDowntimeCategories(
  categoriesToSkip: PnlCategoriesEnum[] = []
): LegendItemType[] {
  const { t } = useTranslation("EOSReport");
  return DOWNTIME_PNL_CATEGORIES.filter(
    item => !categoriesToSkip.includes(item)
  ).map(category => ({
    color: PNL_CATEGORIES_TO_COLORS_MAP[category],
    type: LegendIconEnum.circle,
    value: t(startCase(category)),
  }));
}
